import { Component, ElementRef, inject, effect, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PickerComponent } from '@ctrl/ngx-emoji-mart';
import { v4 as uuidv4 } from 'uuid';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  Message,
  MessageRequest,
  Status,
  StatusValues,
  MediaItem,
  SUPPORTED_MEDIA,
} from '../models/message';
import { mapIncomingMessage, mapBroadcastMessage, isBroadcastMessage, BroadcastMessageData } from '../helpers/message-mapper';
import { ChatStateService } from '../services/chat-state.service';
import { MediaUploadService } from '../services/media-upload.service';
import { MessageService } from '../services/messaging.service';
import { MessageHistoryService } from '../services/history.service';
import { ReceiptWebsocketService } from '../services/receipt-websocket.service';
import { StatusWebsocketService } from '../services/status-websocket.service';
import { BrowserEventBusService } from '../services/event-bus.service';
import { MessageHistory } from './message-history';
import { MediaPreviewComponent } from './media-preview';

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    MatCardModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MessageHistory,
    MediaPreviewComponent,
    PickerComponent,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: '../templates/messaging.component.html',
  styleUrls: ['../templates/messaging.scss'],
})
export class MessagingComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private destroy$ = new Subject<void>();
  private messageService = inject(MessageService);
  private messageHistoryService = inject(MessageHistoryService);
  private receiptWebsocketService = inject(ReceiptWebsocketService);
  private statusWebsocketService = inject(StatusWebsocketService);
  private receiptWebsocketSubscription?: Subscription;
  private statusWebsocketSubscription?: Subscription;
  private matIconRegistry = inject(MatIconRegistry);
  private domSanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private eventBus = inject(BrowserEventBusService);
  private fb = inject(FormBuilder);
  private mediaUploadService = inject(MediaUploadService);
  private idp = environment.idp;
  private messageSids = new Set<string>();
  private pendingOutboundTempSids = new Set<string>();

  public chatState = inject(ChatStateService);

  senderName = '';
  senderPhone = '';
  recipientName = '';
  recipientPhone = '';
  newMessageText = '';
  messages: Record<string, Message[]> = {};
  statuses: Record<string, Status[]> = {};
  selectedFiles: File[] = [];
  uploadedMedia: MediaItem[] = [];
  uploading = false;
  showEmojiPicker = false;
  loadingHistory = false;
  uploadError: string | null = null;
  supportedMediaList = '';

  constructor() {
    console.log('MessagingComponent constructor - setting up WebSocket connections');

    effect(() => {
      console.log('MessagingComponent effect triggered');
      const recipient = this.chatState.recipient();
      const sender = this.chatState.sender();
      const token = this.chatState.token();

      console.log('Effect state check:', {
        recipientPhone: recipient?.phoneNumber,
        sender: !!sender,
        token: !!token,
      });

      if (!recipient?.phoneNumber || !sender || !token) {
        console.log('Missing required data, disconnecting WebSockets');
        this.receiptWebsocketService.disconnect();
        this.statusWebsocketService.disconnect();
        return;
      }

      // Connect WebSocket connections when we have all required data
      console.log('Chat ready - connecting WebSockets for:', recipient.phoneNumber);
      this.receiptWebsocketService.connect(recipient.phoneNumber);
      this.statusWebsocketService.connect(recipient.phoneNumber);
      this.receiptWebsocketSubscription = this.receiptWebsocketService.messages$.subscribe(
        incomingMessages => {
          console.log('Incoming messages:', incomingMessages);
          // TODO: We should unify mapping of incoming/broadcast messages and clean up this typing
          for (const raw of incomingMessages) {
            const isBroadcast = isBroadcastMessage(raw as Record<string, unknown>);
            const msg = isBroadcast
              ? mapBroadcastMessage(raw as unknown as BroadcastMessageData, recipient)
              : mapIncomingMessage(raw, recipient, sender);

            if (this.messageSids.has(msg.sid)) continue;

            // Outbound broadcasts arrive before the HTTP response from handleSendMessage(),
            // so the real SID isn't in messageSids yet. Match against the optimistic message
            // we already displayed (tracked by temp SID) and update it in place.
            if (isBroadcast) {
              const optimistic = this.findPendingOutbound(msg);
              if (optimistic) {
                this.pendingOutboundTempSids.delete(optimistic.sid);
                optimistic.sid = msg.sid;
                optimistic.status = msg.status;
                this.messageSids.add(msg.sid);
                continue;
              }
            }

            this.messageSids.add(msg.sid);
            const dateKey = new Date(msg.sentTimestamp).toLocaleDateString();
            if (!this.messages[dateKey]) {
              this.messages[dateKey] = [];
            }
            // New messages from web socket events don't have receivedTimestamp set.
            // Setting it to current datetime will work.
            if (!msg.receivedTimestamp) msg.receivedTimestamp = new Date();
            this.messages[dateKey].push(msg);
          }
          this.messages = { ...this.messages };
          console.log('Messages after update: ', this.messages);
        },
      );
      this.receiptWebsocketService.optStatus$
        .pipe(takeUntil(this.destroy$))
        .subscribe(optStatus => {
          console.log('Opt status change received:', optStatus);
          this.chatState.isOptedOut.set(optStatus.isOptedOut);
        });
      this.statusWebsocketSubscription = this.statusWebsocketService.statuses$.subscribe(
        incomingStatuses => {
          if (!incomingStatuses?.length) return;
          for (const status of incomingStatuses) {
            for (const dateKey of Object.keys(this.messages)) {
              const dateMessages = this.messages[dateKey];
              const msg = dateMessages.find(m => m.sid === status.MessageSid);
              if (msg && msg.status !== status.MessageStatus) {
                msg.status = status.MessageStatus as StatusValues; // mutate in place
              }
            }
          }
        },
      );
    });
    effect(() => {
      const recipientPhone = this.chatState.recipient()?.phoneNumber;
      const senderPhone = this.chatState.sender()?.phoneNumber;
      if (recipientPhone && senderPhone) {
        this.loadMessageHistory(recipientPhone, senderPhone);
      }
    });
  }

  /** Find the pending optimistic message that matches a broadcast by temp SID + content. */
  private findPendingOutbound(broadcast: Message): Message | undefined {
    for (const dateMessages of Object.values(this.messages)) {
      const match = dateMessages.find(m =>
        this.pendingOutboundTempSids.has(m.sid) &&
        m.content === broadcast.content &&
        m.receiver.phoneNumber === broadcast.receiver.phoneNumber,
      );
      if (match) return match;
    }
    return undefined;
  }

  private loadMessageHistory(to: string, from: string): void {
    this.loadingHistory = true;
    this.messageHistoryService.fetchConversation(to, from).subscribe({
      next: messages => {
        this.loadingHistory = false;
        if (!messages.length) return;
        const grouped: Record<string, Message[]> = {};
        for (const msg of messages) {
          const timestamp = msg.sentTimestamp ?? msg.receivedTimestamp;
          const dateKey = timestamp!.toLocaleDateString();
          if (!grouped[dateKey]) grouped[dateKey] = [];
          if (!this.messageSids.has(msg.sid)) {
            this.messageSids.add(msg.sid);
            grouped[dateKey].push(msg);
          }
        }
        this.messages = grouped;
      },
      error: err => {
        console.error('Failed to fetch message history:', err);
        this.loadingHistory = false;
      },
    });
  }

  getSupportedMediaList(): string {
    return SUPPORTED_MEDIA.map(m => m.extension).join(', ');
  }

  ngOnInit(): void {
    const storedToken = localStorage.getItem('access-token');
    if (storedToken) {
      this.supportedMediaList = this.getSupportedMediaList();
      this.chatState.token.set(storedToken);
    } else {
      console.warn('No token found — user not authenticated.');
      return;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.receiptWebsocketSubscription?.unsubscribe();
    this.statusWebsocketSubscription?.unsubscribe();
    this.receiptWebsocketService.disconnect();
    this.statusWebsocketService.disconnect();
  }

  chatSetupForm = this.fb.group({
    senderName: ['', Validators.required],
    recipientName: ['', Validators.required],
    recipientPhone: ['', [Validators.required, Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/)]],
  });

  addEmoji(event: { emoji: { native: string } }) {
    this.newMessageText = (this.newMessageText || '') + event.emoji.native;
    this.showEmojiPicker = false;
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;

    if (!input.files?.length) return;
    const files = Array.from(input.files);
    this.uploadError = null;
    const unsupportedFiles = files.filter(file => !SUPPORTED_MEDIA.some(m => m.mime === file.type));
    this.supportedMediaList = this.getSupportedMediaList();
    if (unsupportedFiles.length) {
      const names = unsupportedFiles.map(f => f.name).join(', ');
      this.uploadError = `${names}`;
      return;
    }
    this.uploading = true;
    this.mediaUploadService.uploadFiles(files).subscribe({
      next: (media: MediaItem[]) => {
        media.forEach((media, i) => {
          const file = files[i];
          this.uploadedMedia.push({
            url: media.url,
            filename: file.name,
            contentType: file.type,
            size: file.size,
          });
        });
      },
      error: () => {
        this.uploadError = 'Upload failed. Please try again.';
        this.uploading = false;
      },
      complete: () => {
        this.uploading = false;
        if (this.fileInput) this.fileInput.nativeElement.value = '';
      },
    });
  }

  getMediaUrls(media_urls: MediaItem[]): string[] {
    return media_urls.map((m: MediaItem) => m.url);
  }

  removeAttachment(index: number) {
    if (index >= 0 && index < this.uploadedMedia.length) {
      this.uploadedMedia.splice(index, 1);
      this.selectedFiles.splice(index, 1);
      if (this.fileInput) this.fileInput.nativeElement.value = '';
    }
  }

  formatPhoneNumber(controlName: 'recipientPhone') {
    const input = this.chatSetupForm.controls[controlName].value || '';
    const digits = input.replace(/\D/g, '');
    let formatted = digits;
    if (digits.length > 3 && digits.length <= 6) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else if (digits.length > 6) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
    this.chatSetupForm.controls[controlName].setValue(formatted, { emitEvent: false });
  }

  handleMarkAsRead() {
    this.eventBus.sendMessage({ type: 'SF_SMS_READ' });
  }

  handleChatSetup() {
    if (this.chatSetupForm.invalid) {
      this.chatSetupForm.markAllAsTouched();
      return;
    }
    const recipientPhoneDigits = (this.chatSetupForm.value.recipientPhone ?? '').replace(/\D/g, '');
    this.chatState.sender.set({
      name: this.chatSetupForm.value.senderName ?? '',
      phoneNumber: environment.messagingPhone,
    });
    this.chatState.recipient.set({
      name: this.chatSetupForm.value.recipientName ?? '',
      phoneNumber: `+1${recipientPhoneDigits}`,
    });
  }

  formatForDisplay(phoneNumber: string): string {
    const digits = phoneNumber.replace(/\D/g, '').replace(/^1/, '');
    if (digits.length !== 10) return phoneNumber;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }

  handleSendMessage() {
    // Whether or not the message sends successfully, mark the conversation as read.
    this.handleMarkAsRead();
    if (!this.newMessageText && !this.uploadedMedia.length) {
      return;
    }
    if (this.chatState.chatReady() && (this.newMessageText || this.uploadedMedia.length)) {
      const sender = this.chatState.sender()!;
      const receiver = this.chatState.recipient()!;
      const sentTimestamp = new Date();
      const tempSid = uuidv4();
      const newMessageRequest: MessageRequest = {
        sender,
        receiver,
        content: this.newMessageText,
        media_urls: this.uploadedMedia.map(m => m.url),
      };
      // For outbound messages, use empty sender name to show Bardavon logo instead of initials
      const outboundSender = { ...sender, name: '' };
      const newMessage: Message = {
        sid: tempSid,
        sender: outboundSender,
        receiver,
        sentTimestamp,
        content: this.newMessageText,
        status: StatusValues.QUEUED,
        media: this.uploadedMedia.length ? this.uploadedMedia : [],
      };
      const dateKey = sentTimestamp.toLocaleDateString();

      if (!this.messages[dateKey]) {
        this.messages[dateKey] = [];
      }
      this.messages[dateKey].push(newMessage);
      this.pendingOutboundTempSids.add(tempSid);
      this.newMessageText = '';
      this.uploadedMedia = [];
      this.selectedFiles = [];
      this.messageService
        .sendMessage(newMessageRequest)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: res => {
            const realSid = res.messageDetails?.sid;
            const realStatus = res.messageDetails?.status;
            if (!realSid) {
              return;
            }
            this.messageSids.add(realSid);
            this.pendingOutboundTempSids.delete(tempSid);
            const dateKey = sentTimestamp.toLocaleDateString();
            const dateMessages = this.messages[dateKey];
            const msgIndex = dateMessages.findIndex(m => m.sid === tempSid);
            if (msgIndex !== -1) {
              dateMessages[msgIndex] = {
                ...dateMessages[msgIndex],
                sid: realSid,
                status:
                  StatusValues[realStatus?.toUpperCase() as keyof typeof StatusValues] ||
                  StatusValues.SENT,
              };
              this.messages = { ...this.messages };
            }
          },
          error: err => {
            console.error('Error sending message:', err);
            this.pendingOutboundTempSids.delete(tempSid);
            const dateKey = sentTimestamp.toLocaleDateString();
            const dateMessages = this.messages[dateKey];
            const msgIndex = dateMessages.findIndex(m => m.sid === tempSid);
            if (msgIndex !== -1) {
              dateMessages[msgIndex] = {
                ...dateMessages[msgIndex],
                status: StatusValues.FAILED,
              };
              this.messages = { ...this.messages };
            }
          },
        });
    }
  }
}
